import { CircularProgress, FormControlLabel, Radio, RadioGroup, Tooltip, tooltipClasses } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Stack } from '@mui/system';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { DOMAIN_NAME } from '../../../../constants/config';
import { GET_HANDLE_SUGGESTIONS } from '../../../../apis/workspace';
import { URL_HANDLE_AVAILABILITY, WORKSPACE_HANDLE_REGEX } from '../../../../constants/workspaces';
import useDebounce from '../../../../utils/debounce';
import useWorkspaceCreation from '../../../../context/WorkspaceCreationContext';

// regex to validate the workspace handle
const regex = new RegExp(WORKSPACE_HANDLE_REGEX);

const LightTooltip = styled(({ className, ...rest }) => (
  <Tooltip {...rest} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 14,
    padding: '0.9rem',
    border: '1px solid #dadde9',
  },
}));

// append a hidden span to the body to calculate the width of the text
const span = document.createElement('span');
span.style.visibility = 'hidden';
span.style.position = 'absolute';
span.style.whiteSpace = 'pre';
document.body.appendChild(span);

// calculate the width of the text
const calculateTextWidth = (input) => {
  // set the text of the span to the input value
  span.textContent = input.value;

  // set font properties
  span.style.fontSize = window.getComputedStyle(input).fontSize;
  span.style.fontFamily = window.getComputedStyle(input).fontFamily;

  // get the width of the span
  const width = span.offsetWidth;
  return width;
};

const RadioButtonLabel = ({ name }) => <span>{name}<span className="optional-text">.{DOMAIN_NAME}</span></span>;

const RadioButton = ({ disabled, ...props }) => <Radio disabled={disabled} icon={<span className="url-suggestion-radio" />} checkedIcon={<span className="url-suggestion-radio-checked" />} {...props} />;

// renders `Available` or `Unavailable` label based on the availability of the handle
const URLHandleAvailabilityLabel = ({ isHandleAvailable, isHandleValid, workspaceId }) => {
  if (!isHandleValid || workspaceId) return null;
  if (isHandleAvailable) return <span className="available-label">Available</span>;
  return <span className="not-available-label">Unavailable</span>;
};

const URLSuggestionList = (props) => {
  // init
  const { urlSuggestions, isLoading, handleSuggestionChange, workspaceHandle, workspaceId } = props;

  if (isLoading) {
    return (
      <div className="url-suggestions-loading">
        <CircularProgress thickness={5} size={30} />
      </div>
    );
  }

  return (
    <RadioGroup className="radio-group" aria-labelledby="demo-controlled-radio-buttons-group" name="controlled-radio-buttons-group">
      <Stack direction="column">
        {urlSuggestions.map((url, index) => (
          <FormControlLabel
            disabled={!!workspaceId}
            key={`suggestion-${url}-${index}`}
            id="workspace-url-suggestion-radio"
            value={url}
            control={<RadioButton disabled={!!workspaceId} onChange={handleSuggestionChange} checked={workspaceHandle === url} />}
            label={<RadioButtonLabel name={url} />}
          />
        ))}
      </Stack>
    </RadioGroup>
  );
};

const WorkspaceURLHandle = (props) => {
  // init
  const { setToast } = props;
  const { workspaceHandle, workspaceId, setWorkspaceHandle, errors, addError, removeError } = useWorkspaceCreation();

  // constants
  const urlInputRef = useRef(null);

  // mutations/queries
  const [getHandleSuggestions, { loading }] = useLazyQuery(GET_HANDLE_SUGGESTIONS);

  // states
  const [urlSuggestions, setURLSuggestions] = useState([]);
  const [debouncedUrlHandle] = useDebounce(workspaceHandle, 500);

  // adjust the width of the url input based on the text width
  const adjustHandleInputWidth = () => { if (urlInputRef.current) urlInputRef.current.style.width = `${calculateTextWidth(urlInputRef.current)}px`; };

  // validate the handle
  const validateHandle = (handle) => {
    const isValid = regex.test(handle);
    if (isValid) removeError('workspaceHandle');
    else addError('workspaceHandle', 'Sub domain must be 5 to 20 letters long and must be lowercase and not include any special characters or whitespace.');
    return isValid;
  };

  // selects a suggestion from the url suggestion list
  const handleSuggestionChange = (event) => {
    removeError('handleUnavailable');
    setWorkspaceHandle(event.target.value);
  };

  // updates the workspace handle from text input
  const handleOnChange = (event) => {
    validateHandle(event.target.value);
    setWorkspaceHandle(event.target.value);
  };

  // fetch suggestions for the handle and check if handle is available or not
  const fetchSuggestions = async () => {
    try {
      const variables = { handle: workspaceHandle, limit: 3 };
      const { data: { getHandleSuggestions: { status, suggestions } } } = await getHandleSuggestions({ variables });
      if (status === URL_HANDLE_AVAILABILITY.AVAILABLE.description) removeError('handleUnavailable');
      else addError('handleUnavailable', 'Unavailable');
      setURLSuggestions(suggestions);
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while fetching suggestions!', open: true, type: 'error' }));
    }
  };

  // fetch suggestions when the handle changes and no errors
  useEffect(() => { if (!errors.workspaceHandle && !urlSuggestions.includes(debouncedUrlHandle)) fetchSuggestions(); }, [debouncedUrlHandle]);

  // adjust the width of the input when the workspaceHandle changes
  useEffect(() => adjustHandleInputWidth(), [workspaceHandle]);

  return (
    <div className="workspace-creation-step-body">
      <h2 className="step-title">Choose a unique handle</h2>
      {/* Workspace name */}
      <div className="border-cards">
        <div className="border-card">
          <span className="border-card-title">
            Choose a unique handle for accessing your workspace in the browser.
            The handle can be up to 20 letters and numbers, no spaces or special characters.
          </span>
          <div className="border-card-input-container">
            <div className="url-suggestion-label-container">
              <div className="add-url-handle-info">
                <span className="border-card-label">Add URL slug</span>
                <LightTooltip title="Please choose your handle carefully as you will not be able to update it later on." disableFocusListener placement="top">
                  <InfoOutlined />
                </LightTooltip>
              </div>
              <URLHandleAvailabilityLabel workspaceId={workspaceId} isHandleValid={!errors.workspaceHandle} isHandleAvailable={!errors.handleUnavailable} />
            </div>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div id="domain-name-workspace-url-input" onMouseUp={() => urlInputRef.current.focus()} data-disabled={!!workspaceId} data-error={!!errors.handleUnavailable || !!errors.workspaceHandle}>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <input disabled={!!workspaceId} autoFocus ref={urlInputRef} className="shrink-input" value={workspaceHandle} onChange={handleOnChange} placeholder="Enter URL slug" />
              <span className="optional-text">.{DOMAIN_NAME}</span>
            </div>
            {!!errors.workspaceHandle && <span className="error-message">{errors.workspaceHandle}</span>}
          </div>
        </div>
        {/* Workspace URL Suggestions */}
        <div className="border-card">
          <span className="border-card-title">URL slug suggestions</span>
          <URLSuggestionList workspaceId={workspaceId} urlSuggestions={urlSuggestions} isLoading={loading} handleSuggestionChange={handleSuggestionChange} workspaceHandle={workspaceHandle} />
        </div>
      </div>
    </div>
  );
};

URLSuggestionList.propTypes = {
  urlSuggestions: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleSuggestionChange: PropTypes.func.isRequired,
  workspaceHandle: PropTypes.string.isRequired,
  workspaceId: PropTypes.number,
};

URLHandleAvailabilityLabel.propTypes = {
  isHandleAvailable: PropTypes.bool.isRequired,
  isHandleValid: PropTypes.bool.isRequired,
  workspaceId: PropTypes.number,
};

WorkspaceURLHandle.propTypes = {
  setToast: PropTypes.func.isRequired,
};

RadioButtonLabel.propTypes = {
  name: PropTypes.string.isRequired,
};

RadioButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
};

export default WorkspaceURLHandle;
